import React, { useMemo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { handleNavigate } from "../../../hooks/handleNavigate";

import { fetchAssignmentList, setAssignment } from "../assignmentSlice";

import { formatDate, formatScore } from "../../../utils/textUtils";
import { refinePages } from "../../../utils/paginationUtils";

import { typeInfo } from "../../../constants/typeInfo";
import Pagination from "../../../components/Pagination";
import PaperAssignmentModal from "./PaperAssignmentModal";
import LevelTestResultList from "../../leveltest/components/LevelTestResultList";
import ReviewList from "../../leveltest/components/ReviewList";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";

/* ── ECHO 톤 태그 색상 ── */
const TAG_STYLES = {
  vocabulary: { bg: "#E9EFF7", color: "#3E6398" },
  sentence: { bg: "#FBF4D9", color: "#8A6D00" },
  analysis: { bg: "#E8F7EE", color: "#1DB05B" },
  grammar: { bg: "#FDF3E3", color: "#E8920A" },
  comprehensive: { bg: "#EFEBFC", color: "#6B4EE6" },
  paragraph: { bg: "#FCEDE9", color: "#C2452D" },
  paper_assignment: { bg: "#F4F8F7", color: "#3A434E" },
  class_content: { bg: "#F4F8F7", color: "#3A434E" },
};
const getTagStyle = (type) => TAG_STYLES[type] || { bg: "#F4F8F7", color: "#3A434E" };

/* ── D-day 계산 ── */
const getDday = (closedAt) => {
  if (!closedAt) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(closedAt);
  due.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / 86400000);
  if (diff < 0) return { text: "기한 지남", bg: "#F4F8F7", color: "#7A8490" };
  if (diff === 0) return { text: "오늘 마감", bg: "#FCEDE9", color: "#C2452D" };
  if (diff <= 2) return { text: `D-${diff}`, bg: "#FCEDE9", color: "#C2452D" };
  if (diff <= 7) return { text: `D-${diff}`, bg: "#FBF4D9", color: "#8A6D00" };
  return { text: `D-${diff}`, bg: "#F4F8F7", color: "#3A434E" };
};

const AssignmentTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [paperModalOpen, setPaperModalOpen] = useState(false);
  const [selectedPaperAssignment, setSelectedPaperAssignment] = useState(null);

  const { assignmentList, current_page, total_pages } = useSelector(
    (state) => state.assignment
  );
  const { student } = useSelector((state) => state.student);
  const { mode } = useSelector((state) => state.ui);
  const isMobile = mode === "mobile";

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  const handlePageClick = (page) => {
    dispatch(
      fetchAssignmentList({
        studentId: student.id,
        page,
        isFinished: false,
      })
    );
  };

  const handleLearn = async (assignment) => {
    if (assignment.type === 'paper_assignment') {
      setSelectedPaperAssignment(assignment);
      setPaperModalOpen(true);
    } else if (assignment.type === 'class_content') {
      return;
    } else {
      dispatch(setAssignment(assignment));
      handleNavigate({
        url: "/assignment",
        params: { id: assignment.id },
        navigate,
      });
    }
  };

  const handlePaperModalClose = () => {
    setPaperModalOpen(false);
    setSelectedPaperAssignment(null);
    if (student.id) {
      dispatch(fetchAssignmentList({ studentId: student.id, isFinished: false }));
    }
  };

  useEffect(() => {
    if (student.id) dispatch(fetchAssignmentList({ studentId: student.id }));
  }, [dispatch, student]);

  return (
    <div style={{ maxWidth: `${CONTAINER_MAX_WIDTH}px`, margin: "0 auto", padding: isMobile ? "0 12px" : "0 22px" }}>
      {/* 페이지 헤더 */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: isMobile ? "16px 0 12px" : "26px 0 18px" }}>
        <h3 style={{ fontSize: isMobile ? "1.05rem" : "1.22rem", fontWeight: 800, color: "#0E141B", margin: 0 }}>
          미완료 과제
        </h3>
        {assignmentList && assignmentList.length > 0 && (
          <span style={{ fontSize: "0.82rem", fontWeight: 800, color: "#00A396", backgroundColor: "#E5FBF8", padding: "4px 12px", borderRadius: "999px" }}>
            {assignmentList.length}건
          </span>
        )}
      </div>


      {/* 과제 행 카드 */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {/* 레벨테스트 복습. ECHO 계통이라 과제와 별개지만 목록에서는 같이 보여준다. */}
        <LevelTestResultList variant="unfinished" />
        <ReviewList variant="unfinished" />

        {assignmentList && assignmentList.length > 0 ? (
          assignmentList.map((assignment, index) => {
            const dday = getDday(assignment.closed_at);
            const tagStyle = getTagStyle(assignment.type);
            const tagLabel = assignment.type === 'class_content'
              ? assignment.class_content_data?.typeName
              : typeInfo[assignment.type]?.text;
            const progressRate = Number(assignment.progress_rate) || 0;
            const isOverdue = dday?.text === "기한 지남";

            return (
              <div
                key={index}
                style={{
                  backgroundColor: "#FFF",
                  border: "1px solid #E6EDEC",
                  borderRadius: "16px",
                  padding: isMobile ? "12px 14px" : "15px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "10px" : "14px",
                  boxShadow: "0 10px 30px -24px rgba(20,40,50,.2)",
                  flexWrap: "nowrap", // [정렬 v1] 모바일 2줄 방지
                }}
              >
                {/* 날짜 */}
                {!isMobile && (
                  <div style={{ flex: "0 0 auto", width: "96px", lineHeight: 1.5 }}>
                    <div style={{ fontSize: "0.8rem", color: "#7A8490", fontWeight: 600 }}>
                      {formatDate(assignment.created_at)} 출제
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#3A434E", fontWeight: 700 }}>
                      <em style={{ fontStyle: "normal", fontSize: "0.7rem", color: "#B9C1C9", fontWeight: 700, marginRight: "4px" }}>마감</em>
                      {assignment.closed_at ? formatDate(assignment.closed_at) : "-"}
                    </div>
                  </div>
                )}

                {/* 제목 + 태그 */}
                {/* [정렬 v1] 제목이 항상 맨 앞 — 배지 길이에 따라 제목 시작점이
                    들쭉날쭉하던 것(계단 현상) 해소. 배지는 제목 뒤로 모아 한 줄 유지. */}
                <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "7px", flexWrap: "nowrap", overflow: "hidden" }}>
                  {assignment.type === 'class_content' && assignment.class_content_data?.typeEmoji && (
                    <span style={{ flex: "0 0 auto" }}>{assignment.class_content_data.typeEmoji}</span>
                  )}
                  <span style={{
                    fontWeight: 700,
                    fontSize: isMobile ? "0.88rem" : "0.95rem",
                    color: "#0E141B",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}>
                    {assignment.title}
                  </span>
                  {assignment.is_retry && (
                    <span style={{ fontSize: "0.7rem", fontWeight: 800, backgroundColor: "#E8F7EE", color: "#158A48", padding: "3px 9px", borderRadius: "7px", flex: "0 0 auto", whiteSpace: "nowrap" }}>
                      복습 {assignment.retry_info?.retry_count || ''}회차
                    </span>
                  )}
                  {tagLabel && (
                    <span style={{ fontSize: "0.73rem", fontWeight: 700, padding: "3px 10px", borderRadius: "999px", backgroundColor: tagStyle.bg, color: tagStyle.color, flex: "0 0 auto", whiteSpace: "nowrap" }}>
                      {tagLabel}
                    </span>
                  )}
                </div>

                {/* D-day */}
                {dday && assignment.type !== 'class_content' && (
                  <span style={{ fontSize: "0.76rem", fontWeight: 800, padding: "4px 11px", borderRadius: "999px", backgroundColor: dday.bg, color: dday.color, flex: "0 0 auto" }}>
                    {dday.text}
                  </span>
                )}

                {/* 진행률 + 버튼 or 점수 */}
                {assignment.type === 'class_content' ? (
                  <span
                    style={{
                      flex: "0 0 auto",
                      fontWeight: 800,
                      fontSize: "0.92rem",
                      padding: "6px 14px",
                      borderRadius: "999px",
                      fontVariantNumeric: "tabular-nums",
                      backgroundColor: assignment.score >= 80 ? "#E5FBF8" : assignment.score >= 60 ? "#F4F8F7" : "#FCEDE9",
                      color: assignment.score >= 80 ? "#00A396" : assignment.score >= 60 ? "#3A434E" : "#C2452D",
                    }}
                  >
                    {assignment.score !== null ? `${formatScore(assignment.score)}점` : '점수 없음'}
                  </span>
                ) : (
                  <>
                    {assignment.type !== 'paper_assignment' && !isMobile && (
                      <div style={{ width: "110px", flex: "0 0 auto" }}>
                        <div style={{ height: "8px", backgroundColor: "#F4F8F7", borderRadius: "99px", overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${progressRate}%`, backgroundColor: "#00D9C0", borderRadius: "99px" }} />
                        </div>
                        <span style={{ fontSize: "0.73rem", color: "#7A8490", fontWeight: 700, display: "block", marginTop: "4px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {progressRate}%
                        </span>
                      </div>
                    )}
                    <button
                      style={{
                        backgroundColor: isOverdue ? "#FFF" : "#0E141B",
                        color: isOverdue ? "#0E141B" : "#FFF",
                        border: isOverdue ? "1.5px solid #0E141B" : "none",
                        borderRadius: "999px",
                        padding: isMobile ? "8px 15px" : "9px 18px",
                        fontSize: "0.84rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        flex: "0 0 auto",
                      }}
                      onClick={() => handleLearn(assignment)}
                    >
                      {assignment.type === 'paper_assignment' && assignment.is_finished
                        ? (assignment.score !== null ? `${formatScore(assignment.score)}점` : '채점 중')
                        : assignment.type === 'paper_assignment'
                          ? '과제업로드'
                          : progressRate > 0 ? '이어하기' : '시작하기'}
                    </button>
                  </>
                )}
              </div>
            );
          })
        ) : (
          <div
            style={{
              border: "1.5px dashed #E6EDEC",
              borderRadius: "16px",
              padding: "40px 18px",
              textAlign: "center",
              color: "#7A8490",
              fontSize: "0.9rem",
              backgroundColor: "#FFF",
            }}
          >
            미완료 과제가 없어요. 전부 끝냈습니다!
          </div>
        )}
      </div>

      <Pagination
        visiblePages={visiblePages}
        current_page={current_page}
        handlePageClick={handlePageClick}
      />

      <PaperAssignmentModal
        isOpen={paperModalOpen}
        onClose={handlePaperModalClose}
        assignment={selectedPaperAssignment}
      />
    </div>
  );
};

export default AssignmentTable;
